import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { chatService } from "../services/chatService";
import { useChatSocket } from "./useChatSocket";

/**
 * 🕒 Helper function for consistent timestamp parsing and normalization
 */
const parseMessageTimestamp = (message) => {
  try {
    // 🔧 ENHANCED: Handle multiple timestamp formats consistently

    // Priority 1: ISO timestamp (from socket/server)
    if (message.timestamp) {
      const parsed = new Date(message.timestamp);
      if (!isNaN(parsed.getTime())) {
        return parsed.getTime(); // Return Unix timestamp for consistent sorting
      }
    }

    // Priority 2: Date + Time format (from API)
    if (message.date && message.time) {
      // Handle both dot and colon formats: "15.41" or "15:41"
      const normalizedTime = message.time.replace(".", ":");

      // Parse Indonesian date format: "30/10/2025"
      const [day, month, year] = message.date.split("/");
      const [hours, minutes] = normalizedTime.split(":");

      // Create date object with explicit values to avoid timezone issues
      const parsed = new Date(
        parseInt(year),
        parseInt(month) - 1, // Month is 0-based
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        0, // seconds
        0, // milliseconds
      );

      if (!isNaN(parsed.getTime())) {
        return parsed.getTime(); // Return Unix timestamp
      }
    }

    // Priority 3: Use message ID as fallback (maintain relative order)
    if (message.id) {
      // Use base time + ID to maintain rough chronological order
      // This ensures messages with higher IDs are considered newer
      const baseTime = new Date("2025-01-01").getTime();
      return baseTime + message.id * 1000;
    }

    // Last resort: current time
    console.warn(
      "⚠️ Message missing valid timestamp, using current time:",
      message.id || "unknown",
    );
    return Date.now();
  } catch (error) {
    console.error("❌ Error parsing message timestamp:", error, message);

    // Fallback using ID if available
    if (message.id) {
      const baseTime = new Date("2025-01-01").getTime();
      return baseTime + message.id * 1000;
    }
    return Date.now();
  }
};

/**
 * � Helper function for smart message insertion without full re-sort
 */
const insertMessageInOrder = (existingMessages, newMessage) => {
  if (!existingMessages.length) {
    return [newMessage];
  }

  const newTimestamp = parseMessageTimestamp(newMessage);

  // Find insertion point using binary search for better performance
  let left = 0;
  let right = existingMessages.length;

  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    const midTimestamp = parseMessageTimestamp(existingMessages[mid]);

    if (midTimestamp <= newTimestamp) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Insert at the found position
  const result = [...existingMessages];
  result.splice(left, 0, newMessage);
  return result;
};

/**
 * 🔧 Enhanced message merging with performance optimization
 */
const mergeMessagesOptimized = (apiMessages, optimisticMessages) => {
  // If no optimistic messages, return API messages as-is (already sorted)
  if (!optimisticMessages.length) {
    return apiMessages;
  }

  // If API messages have socket messages mixed in, need full sort
  const hasSocketMessages = apiMessages.some((msg) => msg.source === "socket");

  if (hasSocketMessages || optimisticMessages.length > 3) {
    // Full sort for complex scenarios or many optimistic messages
    const allMessages = [...apiMessages, ...optimisticMessages];
    return sortMessagesByTime(allMessages);
  }

  // Optimized insertion for few optimistic messages
  let result = [...apiMessages];
  for (const optimisticMsg of optimisticMessages) {
    result = insertMessageInOrder(result, optimisticMsg);
  }

  return result;
};
const sortMessagesByTime = (messages) => {
  return messages.sort((a, b) => {
    const aTime = parseMessageTimestamp(a);
    const bTime = parseMessageTimestamp(b);

    // Primary sort: by timestamp
    const timeDiff = aTime - bTime;
    if (timeDiff !== 0) {
      return timeDiff; // Oldest to newest
    }

    // Secondary sort: by ID for messages with same timestamp
    // This ensures consistent ordering for rapid messages
    const aId = parseInt(a.id) || 0;
    const bId = parseInt(b.id) || 0;
    return aId - bId;
  });
};

/**
 * 💬 CHAT DETAIL HOOK
 * Real-time chat detail with message management
 */
export const useChatDetail = (conversationId) => {
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    has_more: false,
    total_messages: 0,
  });
  const [sending, setSending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState([]);

  // Refs for stable callbacks
  const messagesRef = useRef(messages);
  const conversationRef = useRef(conversation);

  // Update refs when state changes
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  // Real-time socket handlers
  const socketHandlers = {
    onNewMessage: useCallback(
      (messageData) => {
        // Check if message belongs to current conversation
        // Use direct comparison from messageData to avoid stale closure issues
        const targetConvId = parseInt(conversationId);
        const eventConvId = parseInt(messageData.conversationId);

        console.log("📨 [onNewMessage] Validation check:", {
          targetConvId,
          eventConvId,
          match: targetConvId === eventConvId,
          messageContent: messageData.content?.substring(0, 50),
          senderType: messageData.sender?.type,
        });

        if (!conversationId || eventConvId !== targetConvId) {
          console.warn("❌ Skipping message - conversation ID mismatch:", {
            expected: conversationId,
            expectedParsed: targetConvId,
            received: messageData.conversationId,
            receivedParsed: eventConvId,
            reason: !conversationId ? "No conversationId" : "ID mismatch",
          });
          return;
        }

        // Validation: Check required fields
        if (
          !messageData.id ||
          !messageData.content ||
          !messageData.sender?.type
        ) {
          console.error(
            "❌ Invalid message data - missing required fields:",
            messageData,
          );
          return;
        }

        // Format message for frontend
        const formattedMessage = {
          id: messageData.id,
          sender_type: messageData.sender.type,
          sender_name: messageData.sender.name,
          message: messageData.content,
          time: messageData.timestamp, // 🔥 FIX: Now uses formatted time from backend
          date: messageData.date,
          // 🔥 FIX: Use direct media fields from socket data
          is_media: messageData.is_media || !!messageData.media,
          media_type:
            messageData.media_type || messageData.media?.type || "text",
          media_url: messageData.media_url || messageData.media?.url || null,
          media_info: messageData.media || null,
          status: "delivered",
          source: "socket",
        };

        // 🐛 DEBUG: Log formatted message for troubleshooting
        if (formattedMessage.is_media) {
          console.log("🖼️ Media message formatted:", {
            id: formattedMessage.id,
            sender_type: formattedMessage.sender_type,
            is_media: formattedMessage.is_media,
            media_type: formattedMessage.media_type,
            media_url: formattedMessage.media_url,
            media_info: formattedMessage.media_info,
            originalSocketData: {
              is_media: messageData.is_media,
              media_type: messageData.media_type,
              media_url: messageData.media_url,
              media: messageData.media,
            },
          });
        }

        setMessages((prev) => {
          // Check if message already exists (prevent duplicates)
          const exists = prev.some((msg) => msg.id === formattedMessage.id);
          if (exists) {
            return prev;
          }

          // 🔧 ENHANCED: Use smart insertion instead of full sort for better performance
          return insertMessageInOrder(prev, formattedMessage);
        });

        // 🔧 ENHANCED: Improved optimistic message cleanup
        if (messageData.tempId) {
          console.log(
            "🧹 Removing optimistic message by tempId:",
            messageData.tempId,
          );
          setOptimisticMessages((prev) => {
            const filtered = prev.filter(
              (msg) => msg.tempId !== messageData.tempId,
            );
            console.log(
              "🧹 Optimistic messages after tempId removal:",
              filtered.length,
            );
            return filtered;
          });
        } else {
          // 🔧 ENHANCED: Multi-criteria fallback cleanup
          console.log("🧹 Attempting enhanced optimistic removal:", {
            socketContent: messageData.content?.substring(0, 20),
            socketSenderType: messageData.sender.type,
            socketTimestamp: messageData.timestamp,
          });

          setOptimisticMessages((prev) => {
            const now = Date.now();
            const filtered = prev.filter((msg) => {
              // Criteria 1: Exact content and sender match
              const contentMatch =
                msg.message === messageData.content &&
                msg.sender_type === messageData.sender.type;

              // Criteria 2: Recent message (within 30 seconds) - prevent old message cleanup
              const isRecent = msg._timestamp && now - msg._timestamp < 30000;

              // Criteria 3: Content preview match (for better matching)
              const contentPreview = messageData.content?.substring(0, 20);
              const previewMatch = msg._contentPreview === contentPreview;

              // Match if content matches AND is recent, OR if preview matches AND is recent
              const shouldRemove =
                (contentMatch && isRecent) || (previewMatch && isRecent);

              if (shouldRemove) {
                console.log("🧹 Matched optimistic message for removal:", {
                  tempId: msg.tempId,
                  contentMatch,
                  previewMatch,
                  isRecent,
                  age: msg._timestamp ? now - msg._timestamp : "unknown",
                });
              }

              return !shouldRemove;
            });

            console.log(
              "🧹 Optimistic messages after enhanced removal:",
              filtered.length,
            );
            return filtered;
          });
        }
      },
      [conversationId],
    ),

    onAIResponse: useCallback((responseData) => {
      // 🐛 DEBUG: Log AI response for media troubleshooting
      if (process.env.NODE_ENV === "development") {
        console.log("🤖 AI Response Debug:", {
          hasMediaUrl: !!responseData.media_url,
          hasMediaFlag: !!responseData.hasMedia,
          mediaUrl: responseData.media_url?.substring(0, 50) + "...",
          responseKeys: Object.keys(responseData),
        });
      }

      // Add AI response as message with consistent timestamp format
      const currentTimestamp = Date.now();
      const aiMessage = {
        id: responseData.messageId || `ai_${currentTimestamp}`,
        sender_type: "ai",
        sender_name: "AI Assistant",
        message: responseData.message,
        // 🔧 FIXED: Use consistent timestamp format for AI messages
        timestamp: new Date(currentTimestamp).toISOString(),
        time: new Date(currentTimestamp).toLocaleTimeString("id-ID", {
          hour: "2-digit",
          minute: "2-digit",
        }),
        date: new Date(currentTimestamp).toLocaleDateString("id-ID"),
        // 🔥 FIX: Proper media handling for socket AI responses
        is_media: !!responseData.hasMedia || !!responseData.media_url,
        media_type:
          responseData.media_type ||
          (responseData.hasMedia || responseData.media_url ? "image" : "text"),
        media_url: responseData.media_url || null,
        media_info:
          responseData.hasMedia || responseData.media_url
            ? {
                type: responseData.media_type || "image",
                url: responseData.media_url,
              }
            : null,
        status: "delivered",
        cost: responseData.cost,
        model: responseData.model,
        processing_time: responseData.processing_time,
        source: "socket",
      };

      setMessages((prev) => {
        const exists = prev.some((msg) => msg.id === aiMessage.id);
        if (exists) return prev;

        // 🔧 ENHANCED: Use smart insertion for AI messages too
        return insertMessageInOrder(prev, aiMessage);
      });
    }, []),

    onConversationUpdated: useCallback(
      (updateData) => {
        if (
          !conversationId ||
          updateData.conversationId !== parseInt(conversationId)
        ) {
          return;
        }

        setConversation((prev) =>
          prev
            ? {
                ...prev,
                status: updateData.status,
                escalation_reason: updateData.escalation_reason,
                last_message: updateData.last_message,
              }
            : null,
        );
      },
      [conversationId],
    ),

    onConversationAssigned: useCallback(
      (assignData) => {
        if (
          !conversationId ||
          assignData.conversationId !== parseInt(conversationId)
        ) {
          return;
        }

        setConversation((prev) =>
          prev
            ? {
                ...prev,
                status: "assigned",
                assigned_to: assignData.assignedTo,
                updated_at: new Date().toISOString(),
              }
            : null,
        );
      },
      [conversationId],
    ),

    onConversationResolved: useCallback(
      (resolveData) => {
        if (
          !conversationId ||
          resolveData.conversationId !== parseInt(conversationId)
        ) {
          return;
        }

        setConversation((prev) =>
          prev
            ? {
                ...prev,
                status: "active",
                assigned_to: null,
                resolved_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              }
            : null,
        );
      },
      [conversationId],
    ),

    onConversationDisabled: useCallback(
      (disableData) => {
        if (
          !conversationId ||
          disableData.conversationId !== parseInt(conversationId)
        ) {
          return;
        }

        setConversation((prev) =>
          prev
            ? {
                ...prev,
                status: "disable",
                updated_at: new Date().toISOString(),
              }
            : null,
        );
      },
      [conversationId],
    ),
  };

  // Initialize socket connection
  const { isConnected } = useChatSocket({
    conversationId: parseInt(conversationId),
    ...socketHandlers,
  });

  // Fetch initial messages
  const fetchMessages = useCallback(
    async (reset = false) => {
      if (!conversationId) {
        // Clear states when no conversation ID
        setMessages([]);
        setConversation(null);
        setError(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const currentPage = reset ? 1 : pagination.page;
        const response = await chatService.getMessages(conversationId, {
          page: currentPage,
          limit: pagination.limit,
        });

        // Improved response validation
        if (response && response.messages !== undefined) {
          const formattedMessages = Array.isArray(response.messages)
            ? response.messages.map((msg) => ({
                id: msg.id,
                sender_type: msg.sender_type,
                sender_name: msg.sender_name,
                message: msg.message,
                time: msg.time,
                date: msg.date,
                is_media: msg.is_media,
                media_type: msg.media_type,
                media_url: msg.media_url,
                media_info: msg.media_info,
                status: "read",
              }))
            : [];

          const orderedMessages = formattedMessages.reverse();

          if (reset) {
            setMessages(orderedMessages);
          } else {
            setMessages((prev) => {
              const combined = [...orderedMessages, ...prev];
              return combined;
            });
          }

          // Set conversation data with fallback
          setConversation(response.conversation || null);

          // Set pagination with defaults
          setPagination({
            page: response.pagination?.page || 1,
            limit: response.pagination?.limit || 50,
            has_more: response.pagination?.has_more || false,
            total_messages:
              response.pagination?.total_messages || orderedMessages.length,
          });
        } else {
          // Handle empty or invalid response gracefully
          setMessages([]);
          setConversation(null);
          setError("No conversation data found");
        }
      } catch (err) {
        console.error("Error fetching messages:", err);

        // More specific error messages
        if (err.response?.status === 404) {
          setError("Conversation not found");
        } else if (err.response?.status >= 500) {
          setError("Server error. Please try again later");
        } else {
          setError("Failed to load messages. Please refresh");
        }

        // Reset states on error
        setMessages([]);
        setConversation(null);
      } finally {
        setLoading(false);
      }
    },
    [conversationId, pagination.page, pagination.limit],
  );

  // Load more messages (for pagination)
  const loadMore = useCallback(async () => {
    if (!pagination.has_more || loading) return;

    setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    await fetchMessages(false);
  }, [pagination.has_more, loading, fetchMessages]);

  // Send message
  const sendMessage = useCallback(
    async (messageText, operatorName = null, mediaUrl = null) => {
      if (!conversationId || (!messageText.trim() && !mediaUrl) || sending)
        return;

      // Get operator name from localStorage or use provided value
      // Remove fallback to "Human Agent" to ensure localStorage value is used
      const senderName =
        operatorName || localStorage.getItem("operatorName") || "";

      if (!senderName) {
        throw new Error(
          "Operator name not found. Please set up your operator name first.",
        );
      }

      try {
        setSending(true);

        // Create optimistic message with enhanced tempId
        const currentTimestamp = Date.now();
        const contentHash = messageText.trim().substring(0, 20); // Use content preview for matching
        const tempId = `temp_${currentTimestamp}_${
          contentHash.length
        }_${senderName.substring(0, 5)}`;

        const optimisticMessage = {
          id: tempId,
          sender_type: "operator",
          sender_name: senderName,
          message: messageText.trim() || "",
          // 🔧 FIXED: Use consistent timestamp format
          timestamp: new Date(currentTimestamp).toISOString(), // ISO format for consistency
          time: new Date(currentTimestamp).toLocaleTimeString("id-ID", {
            hour: "2-digit",
            minute: "2-digit",
          }),
          date: new Date(currentTimestamp).toLocaleDateString("id-ID"),
          is_media: !!mediaUrl,
          media_type: mediaUrl ? "image" : "text",
          media_url: mediaUrl || null,
          status: "sending",
          tempId: tempId,
          // Add metadata for better cleanup matching
          _optimistic: true,
          _timestamp: currentTimestamp,
          _contentPreview: contentHash,
        };

        // 🐛 DEBUG: Log optimistic message creation
        if (mediaUrl) {
          console.log("🚀 Creating optimistic message with media:", {
            tempId: tempId,
            is_media: optimisticMessage.is_media,
            media_type: optimisticMessage.media_type,
            media_url: optimisticMessage.media_url,
            messageText: messageText.trim(),
          });
        }

        // Add optimistic message
        setOptimisticMessages((prev) => [...prev, optimisticMessage]);

        console.log("🚀 Optimistic message created:", {
          tempId,
          timestamp: currentTimestamp,
          contentPreview: contentHash,
          sender: senderName,
          hasMedia: !!mediaUrl,
        });

        // 🔧 ENHANCED: More aggressive cleanup timeout for better UX
        const cleanupTimeout = setTimeout(() => {
          setOptimisticMessages((prev) => {
            const filtered = prev.filter((msg) => msg.tempId !== tempId);
            console.log("🧹 Auto-removed stale optimistic message:", {
              tempId,
              remaining: filtered.length,
            });
            return filtered;
          });
        }, 10000); // Reduced from 15s to 10s

        // Send via API
        const payload = {
          message: messageText.trim() || "",
          operatorName: senderName,
          tempId: tempId, // Send tempId to backend for matching
        };

        // Add mediaUrl if present
        if (mediaUrl) {
          payload.mediaUrl = mediaUrl;
        }

        const response = await chatService.sendMessage(conversationId, payload);

        console.log("✅ Message sent successfully:", response);

        // Clear timeout since message was sent successfully
        clearTimeout(cleanupTimeout);

        // Remove optimistic message (real message will come via socket)
        setOptimisticMessages((prev) =>
          prev.filter((msg) => msg.tempId !== tempId),
        );

        return response;
      } catch (err) {
        console.error("Error sending message:", err);

        // Remove optimistic message and show error
        setOptimisticMessages((prev) =>
          prev.filter((msg) => msg.tempId !== tempId),
        );
        throw new Error("Failed to send message");
      } finally {
        setSending(false);
      }
    },
    [conversationId, sending],
  );

  // Upload image
  const uploadImage = useCallback(
    async (imageFile) => {
      if (!conversationId) {
        throw new Error("No conversation selected");
      }

      if (
        !conversation ||
        (conversation.status !== "assigned" &&
          conversation.status !== "disable")
      ) {
        throw new Error("Please assign conversation first to upload images");
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(imageFile.type)) {
        throw new Error("Please select image files only (jpg, png, gif, webp)");
      }

      // Validate file size (optional client-side check)
      const maxSize = 50 * 1024 * 1024; // 50MB
      if (imageFile.size > maxSize) {
        throw new Error("Image file is too large. Maximum size is 50MB");
      }

      try {
        console.log("📷 Uploading image:", imageFile.name);
        const response = await chatService.uploadImage(imageFile);
        console.log("✅ Image uploaded successfully:", response);
        return response;
      } catch (err) {
        console.error("❌ Error uploading image:", err);
        throw new Error("Failed to upload image. Please try again");
      }
    },
    [conversationId, conversation],
  );

  // File validation helper
  const validateImageFile = useCallback((file) => {
    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
    ];

    if (!file) {
      return { valid: false, error: "No file selected" };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: "Please select image files only (jpg, png, gif, webp)",
      };
    }

    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: "Image file is too large. Maximum size is 50MB",
      };
    }

    return { valid: true, error: null };
  }, []);

  // Assign conversation
  const assignConversation = useCallback(
    async (operatorName = null) => {
      if (!conversationId) return;

      // Get operator name from localStorage or use provided value
      // Remove fallback to ensure localStorage value is used
      const assignedOperator =
        operatorName || localStorage.getItem("operatorName") || "";

      if (!assignedOperator) {
        throw new Error(
          "Operator name not found. Please set up your operator name first.",
        );
      }

      try {
        const response = await fetch(
          `${
            import.meta.env.VITE_API_BASE_URL
          }/conversations/${conversationId}/assign`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ operatorName: assignedOperator }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to assign conversation");
        }

        const data = await response.json();
        console.log("✅ Conversation assigned:", data);

        // 🔥 IMMEDIATE UI UPDATE: Update local state immediately for instant UI response
        setConversation((prev) =>
          prev
            ? {
                ...prev,
                status: "assigned",
                assigned_to: assignedOperator,
                updated_at: new Date().toISOString(),
              }
            : null,
        );

        return data;
      } catch (err) {
        console.error("❌ Error assigning conversation:", err);

        // 🔥 ROLLBACK: Revert state change if API call failed
        setConversation((prev) =>
          prev && prev.status === "assigned"
            ? { ...prev, status: "active", assigned_to: null }
            : prev,
        );

        throw err;
      }
    },
    [conversationId],
  );

  // Resolve conversation
  const resolveConversation = useCallback(async () => {
    if (!conversationId) return;

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/conversations/${conversationId}/resolve`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to resolve conversation");
      }

      const data = await response.json();
      console.log("✅ Conversation resolved:", data);

      // 🔥 IMMEDIATE UI UPDATE: Update local state immediately for instant UI response
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              status: "active",
              assigned_to: null,
              resolved_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : null,
      );

      return data;
    } catch (err) {
      console.error("❌ Error resolving conversation:", err);

      // 🔥 ROLLBACK: Revert state change if API call failed
      // Store previous state before optimistic update to rollback properly
      setConversation((prev) => {
        if (prev && prev.status === "active") {
          // If current state is active (optimistic update), rollback to assigned
          const fallbackOperator =
            localStorage.getItem("operatorName") || "Human Agent";
          return {
            ...prev,
            status: "assigned",
            assigned_to: prev.assigned_to || fallbackOperator,
            resolved_at: null,
          };
        }
        return prev;
      });

      throw err;
    }
  }, [conversationId]);

  // Disable conversation
  const disableConversation = useCallback(async () => {
    if (!conversationId) return;

    try {
      const response = await fetch(
        `${
          import.meta.env.VITE_API_BASE_URL
        }/conversations/${conversationId}/disable`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to disable conversation");
      }

      const data = await response.json();
      console.log("✅ Conversation disabled:", data);

      // 🔥 IMMEDIATE UI UPDATE: Update local state immediately for instant UI response
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              status: "disable",
              updated_at: new Date().toISOString(),
            }
          : null,
      );

      return data;
    } catch (err) {
      console.error("❌ Error disabling conversation:", err);

      // 🔥 ROLLBACK: Revert state change if API call failed
      setConversation((prev) => {
        if (prev && prev.status === "disable") {
          // If current state is disable (optimistic update), rollback to assigned
          const fallbackOperator =
            localStorage.getItem("operatorName") || "Human Agent";
          return {
            ...prev,
            status: "assigned",
            assigned_to: prev.assigned_to || fallbackOperator,
          };
        }
        return prev;
      });

      throw err;
    }
  }, [conversationId]);

  // Initial fetch when conversationId changes
  useEffect(() => {
    if (
      conversationId &&
      conversationId !== "undefined" &&
      conversationId !== ""
    ) {
      fetchMessages(true);
    } else {
      setMessages([]);
      setConversation(null);
      setOptimisticMessages([]);
      setError(null);
      setLoading(false);
    }
  }, [conversationId]);

  // 🔧 ENHANCED: Optimized message merging strategy with debugging
  const finalMessages = useMemo(() => {
    const result = mergeMessagesOptimized(messages, optimisticMessages);

    // Debug logging for monitoring
    if (
      process.env.NODE_ENV === "development" &&
      optimisticMessages.length > 0
    ) {
      console.log("📋 Message merge debug:", {
        apiMessages: messages.length,
        optimisticMessages: optimisticMessages.length,
        finalCount: result.length,
        optimisticIds: optimisticMessages.map((m) => m.tempId),
        lastMessageTimestamp:
          result.length > 0
            ? parseMessageTimestamp(result[result.length - 1])
            : null,
      });
    }

    return result;
  }, [messages, optimisticMessages]);

  return {
    // Data
    messages: finalMessages,
    conversation,
    loading,
    error,
    sending,
    pagination,

    // Connection status
    isConnected,

    // Actions
    sendMessage,
    loadMore,
    assignConversation,
    resolveConversation,
    disableConversation,
    uploadImage,
    validateImageFile,
    refresh: () => fetchMessages(true),
  };
};
